/**
 * Shared upload behaviour for the ASN document panels (CR-7163 (b)).
 *
 * Both `ASNDocumentsTab` (ASN-level) and `ASNLineDocuments` (per delivery
 * line) previously carried a byte-identical single-file `handleFileUpload`
 * with no `multiple` attribute and no drag handlers — so dragging paperwork
 * onto either panel just made the browser open the files. This hook owns the
 * validation and the multi-file batch upload for both, so the two panels
 * cannot drift apart again.
 *
 * Customer review 11 Sep (drag and drop everywhere): the drag handling that
 * used to live here moved into the shared FileDropZone, which both panels
 * now render; this hook only receives the files (`uploadFiles`).
 */
import { useCallback, useRef, useState } from 'react';
import { purchasingAPI } from '../../lib/api';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { fileTypeRefusedMessage } from '../../lib/fileTypes';
import { toast } from 'sonner';

/** Extensions the ASN document endpoint accepts. */
export const ASN_ALLOWED_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv',
  'zip', 'rar', '7z', 'jpg', 'jpeg', 'png', 'gif',
];

/** `accept` attribute matching ASN_ALLOWED_EXTENSIONS. */
export const ASN_ACCEPT_ATTR = ASN_ALLOWED_EXTENSIONS
  .map(e => `.${e}`)
  .join(',');

export const ASN_MAX_FILE_SIZE = 50 * 1024 * 1024;

/** Matches the Documents module's per-drop cap. */
export const ASN_MAX_FILES_PER_UPLOAD = 20;

/**
 * Validate one file against the ASN document rules.
 *
 * @param file - The file to check
 * @returns An error message, or null when the file is acceptable
 */
export function validateASNDocument(file: File): string | null {
  if (file.size > ASN_MAX_FILE_SIZE) {
    return 'exceeds the 50MB limit';
  }
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext || !ASN_ALLOWED_EXTENSIONS.includes(ext)) {
    return fileTypeRefusedMessage(ASN_ALLOWED_EXTENSIONS);
  }
  return null;
}

interface UseASNDocumentUploadOptions {
  /**
   * Builds the multipart body for one file. Callers differ only in whether
   * they attach a `delivery_line`, so the hook takes the whole body from them
   * rather than trying to guess the shape.
   */
  buildFormData: (file: File) => FormData;
  /** Called once after a batch finishes, if anything uploaded. */
  onUploaded: () => void | Promise<void>;
}

/** A file that did not upload, retained so the user can see which. */
export interface FailedUpload {
  name: string;
  reason: string;
}

interface UseASNDocumentUploadResult {
  uploading: boolean;
  /** `{ done, total }` while a batch runs; total is 0 when idle. */
  progress: { done: number; total: number };
  /**
   * Files from the last batch that did not upload. Retained rather than
   * toast-only: in a 20-file batch the toasts scroll away and the user is
   * left unable to tell which of their paperwork is missing.
   */
  failed: FailedUpload[];
  /** Clears the retained failure list. */
  dismissFailures: () => void;
  /** Validate and upload a batch — wire to FileDropZone's onFiles. */
  uploadFiles: (files: File[]) => Promise<void>;
}

/**
 * Multi-file upload for an ASN document panel.
 *
 * Files upload sequentially rather than in parallel: the endpoint takes one
 * file per request, and a burst of 20 concurrent multipart POSTs is a good way
 * to get rate-limited. Invalid files are reported by name and skipped — the
 * valid ones still upload, so one stray file in a batch of paperwork doesn't
 * force the user to re-drag everything.
 *
 * @param options - Body builder and post-batch refresh callback
 * @returns Upload state plus the batch uploader for the drop zone
 *
 * @example
 * const upload = useASNDocumentUpload({
 *   buildFormData: (file) => { const fd = new FormData(); fd.append('asn', String(asnId)); return fd; },
 *   onUploaded: fetchDocuments,
 * });
 * <FileDropZone multiple accept={ASN_ACCEPT_ATTR} onFiles={upload.uploadFiles} />
 */
export function useASNDocumentUpload(
  options: UseASNDocumentUploadOptions,
): UseASNDocumentUploadResult {
  const { buildFormData, onUploaded } = options;
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [failed, setFailed] = useState<FailedUpload[]>([]);
  // The drop zone stays live during a batch (unlike the button, which is
  // disabled), so a second drop mid-run would otherwise start an overlapping
  // batch — resetting the first one's progress counter and clearing
  // `uploading` while its POSTs are still in flight.
  const busy = useRef(false);

  const uploadFiles = useCallback(async (fileList: File[]) => {
    if (fileList.length === 0) return;
    if (busy.current) {
      toast.error('An upload is already in progress — wait for it to finish.');
      return;
    }

    // Clear the previous batch's failures before any early return, or a
    // stale red list sits next to a toast about the new attempt.
    setFailed([]);

    if (fileList.length > ASN_MAX_FILES_PER_UPLOAD) {
      toast.error(
        `You can upload up to ${ASN_MAX_FILES_PER_UPLOAD} files at a time. You selected ${fileList.length}.`,
      );
      return;
    }

    const valid: File[] = [];
    const problems: FailedUpload[] = [];
    for (const file of fileList) {
      const problem = validateASNDocument(file);
      if (problem) {
        problems.push({ name: file.name, reason: problem });
        toast.error(`${file.name}: ${problem}`);
      } else {
        valid.push(file);
      }
    }
    setFailed(problems);
    if (valid.length === 0) return;

    busy.current = true;
    setUploading(true);
    setProgress({ done: 0, total: valid.length });
    let succeeded = 0;

    try {
      for (const file of valid) {
        try {
          const formData = buildFormData(file);
          formData.append('file', file);
          formData.append('file_name', file.name);
          await purchasingAPI.uploadASNDocument(formData);
          succeeded += 1;
        } catch (error: unknown) {
          console.error('Failed to upload ASN document:', error);
          const reason = getApiErrorMessage(error, 'upload failed');
          setFailed(prev => [...prev, { name: file.name, reason }]);
          toast.error(`${file.name}: ${reason}`);
        } finally {
          setProgress(p => ({ ...p, done: p.done + 1 }));
        }
      }

      if (succeeded > 0) {
        // One refresh per batch, not per file.
        await onUploaded();
        toast.success(
          succeeded === 1
            ? 'Document uploaded successfully'
            : `${succeeded} documents uploaded successfully`,
        );
      }
    } finally {
      busy.current = false;
      setUploading(false);
      setProgress({ done: 0, total: 0 });
    }
  }, [buildFormData, onUploaded]);

  const dismissFailures = useCallback(() => setFailed([]), []);

  return {
    uploading,
    progress,
    failed,
    dismissFailures,
    uploadFiles,
  };
}
